import axios from "axios";
import { Graph } from "graphlib";
import _ from "lodash";
import { mean, median, variance } from "mathjs";

export enum NodeType {
  "DRUG" = "drug",
  "KINASE" = "kinase",
  "CYTOKINE" = "cytokine",
  "NA" = "na"
}

export interface IVisualizationModel {
  data: Data,
  nodeType: NodeType,
  selected: Array<string>
}

export interface CytoListType {
  cytokine: string,
  score: number
}

export interface DrugCytokineScoreType {
  drug: string;
  score: number;
  cytokine: string;
}

export interface DrugFilteredDataType {
  cytokines: CytoListType[],
  drug: string,
  mean: number,
  median: number,
  numCytokines: number,
  kiss: string | number,
  percent: number,
  fdaApproved: string,
}

export interface KinaseFilteredDataType {
  cytokine: string,
  kinase: string
}

export class Node {
  constructor(node: string) {
    this._id = node;
    const nodeParts = node.split("_");
    const type = nodeParts.shift();
    const name = nodeParts.join("_");
    this._type = type === NodeType.DRUG ? NodeType.DRUG :
      type === NodeType.CYTOKINE ? NodeType.CYTOKINE :
        type === NodeType.KINASE ? NodeType.KINASE :
          NodeType.NA;
    this._name = name;
  }
  private _type: NodeType;
  public get type(): NodeType { return this._type; }

  private _name: string;
  public get name(): string { return this._name; }

  private _id: string;
  public get id(): string { return this._id; }
}


export class NodeEdge {
  static from(source: string, target: string): NodeEdge {
    return new NodeEdge(new Node(source), new Node(target))
  }
  constructor(node1: Node, node2: Node) {
    this._node1 = node1;
    this._node2 = node2;
  }
  private _node1: Node;
  public get node1(): Node { return this._node1 }
  private _node2: Node;
  public get node2(): Node { return this._node2 }
}

export interface IFilteredData {
  edges: Array<NodeEdge>,
  drugs: Array<string>,
  kinases: Array<string>,
  cytokines: Array<string>
}


export class Data {

  constructor() {
    this._graph = new Graph();
    this._drugs = [];
    this._fdaApproved = [];
    this._kinase = [];
    this._cytokine = [];
  }

  private _graph: Graph;
  public get graph(): Graph { return this._graph; }

  private _drugs: Array<string>;
  public get drugs(): Array<string> { return this._drugs; }

  private _fdaApproved: Array<string>;
  public get fdaApproved(): Array<string> { return this._fdaApproved; }

  private _kinase: Array<string>;
  public get kinase(): Array<string> { return this._kinase; }

  private _cytokine: Array<string>;
  public get cytokine(): Array<string> { return this._cytokine; }

  private _drugMatrix: Array<Array<number>> = [];
  private _drugCytokineLabels: Array<string> = [];

  private _kinaseMatrix: Array<Array<number>> = [];
  private _kinaseCytokineNames: Array<string> = [];
  
  public getDrugs(cytokine: string): Array<{ drug: string, score: number }> {
    const cytoIndex = this._drugCytokineLabels.findIndex(v => v === cytokine);
    return this._drugs
      .map((drug, i) => ({ drug, score: this._drugMatrix[i][cytoIndex] }))
      .filter(v => v.score < 100 && v.score > 0);
  }

  public getKinases(cytokine: string, cytokines: Array<string>, kianses: Array<string>, matrix: Array<Array<number>>): Array<any> {
    const cytoIndex = cytokines.findIndex(v => v === cytokine);
    return kianses.map((kinase, i) => ({ kinase, row: matrix[i][cytoIndex]})) 
}

  public getNodesByType(nodeType: NodeType): Array<string> {
    if (nodeType === NodeType.CYTOKINE) return this.cytokine;
    if (nodeType === NodeType.KINASE) return this.kinase;
    if (nodeType === NodeType.DRUG) return this.drugs;
    return [];
  }

  public async load(): Promise<Data> {
    // load
    const data = await Promise.all([
      axios.get("/kincyte/data/cyto_kinase.tsv"),
      axios.get("/kincyte/data/cyto-drug.tsv")
    ]);
    let drugMatrix = data[1].data.split("\r\n").map((row: string) => row.split("\t"))
    this._drugCytokineLabels = drugMatrix.shift().slice(4).map((v: string) => v.toLowerCase().trim())
    this._drugs = drugMatrix.map((row: Array<string>) => row.shift()?.replaceAll("\"", ""))
    this._fdaApproved = drugMatrix.map((row: Array<string>) => row.shift() || "")
    const drugThreshold50 = drugMatrix.map((row: Array<string>) => parseFloat(row.shift() || ""))
    const drugThreshold70 = drugMatrix.map((row: Array<string>) => parseFloat(row.shift() || ""))
    this._drugMatrix = drugMatrix.map((row: Array<string>) => row.map(col => parseFloat(col)));

    let kinaseMatrix = data[0].data.split("\r\n").map((row: string) => row.split("\t"))
    this._kinaseCytokineNames = kinaseMatrix.shift().slice(4).map((v: string) => v.toLowerCase().trim())
    this._kinase = kinaseMatrix.map((row: Array<string>) => row.shift()).slice(2);
    const cytokineSymbol = kinaseMatrix.shift().slice(1);
    const kinasePerModel = kinaseMatrix.shift().slice(1);
    const cytokinesPerModel = kinaseMatrix.map((row: Array<string>) => row.shift());
    this._kinaseMatrix = kinaseMatrix.map((row: Array<string>) => row.splice(2).map(col => parseFloat(col)));

    this._cytokine = Array.from(new Set([...this._kinaseCytokineNames, ...this._drugCytokineLabels]));

    // Nodes
    this._drugs.forEach((drug: string) => { this._graph.setNode(`${NodeType.DRUG}_${drug}`); });
    this._cytokine.forEach((cyto: string) => { this._graph.setNode(`${NodeType.CYTOKINE}_${cyto}`); });
    this._kinase.forEach((kinase: string) => { this._graph.setNode(`${NodeType.KINASE}_${kinase}`) });

    // Add Drug To Cyto Edges
    this._drugCytokineLabels.forEach((cyto: string, cytoIndex: number) => {
      this._drugs.forEach((drugName: string, drugIndex: number) => {
        if (drugMatrix[drugIndex][cytoIndex] < 100) {
          this._graph.setEdge(NodeType.DRUG + "_" + drugName, NodeType.CYTOKINE + "_" + cyto, drugMatrix[drugIndex][cytoIndex]);
        }
      });
    });

    // Add Kinase To Cyto Edges
    this._kinaseCytokineNames.forEach((cyto: string, cytoIndex: number) => {
      this._kinase.forEach((kinaseName: string, kinaseIndex: number) => {
        if (this._kinaseMatrix[kinaseIndex][cytoIndex] > 0) {
          this._graph.setEdge(NodeType.CYTOKINE + "_" + cyto, NodeType.KINASE + "_" + kinaseName, this._kinaseMatrix[kinaseIndex][cytoIndex]);
        }
      });
    });
    return this;
  }

  public calculateKISS(drug: string, 
    includedDrugCytokineScore: DrugCytokineScoreType[], 
    excludedDrugCytokineScore: DrugCytokineScoreType[] ): number | string {
    // Calculate the KISS (Kinase Inhibition Selectivity Score) for a single drug
    function gmean(inp: number[]) {
        let product = 1;
        const n = inp.length;
   
        for (let i = 0; i < n; i++)
            product = product * inp[i];

        let gm = Math.pow(product, 1 / n);
        return gm;
    }

    function artm(inp: number[]) {
      const sum = inp.reduce((partialSum, a) => partialSum + a, 0);
      return (sum / inp.length)
    }
    
    function slo(inp: number[], num: number[]) {
      const max = Math.max(...inp);
      const sum = num.reduce((partialSum, a) => partialSum + a, 0);
      return max**2 / 2 * sum
    }

    const included = includedDrugCytokineScore.filter(v => v.drug === drug).map(v => v.score);
    const is0 = gmean(included);

    const excluded = excludedDrugCytokineScore.filter(v => v.drug === drug).map(v => v.score).filter(v => v !== 0);

    if (excluded.length === 0) return 'N/A'
    const ip1 = artm(excluded);

    const total = included.concat(excluded);

    const sy2 = variance(...excluded);
    const sz2 = variance(...total);
    const sl2 = slo(excluded, total);
    const ip2 = (sy2-sl2) / (sz2-sl2);
    const ip = (ip1 + 100*(ip2** 5)) / 2;
    const kissScore = is0-ip;

    return kissScore
  }


  public filterForDrugs(selected: Array<Node>): Array<DrugFilteredDataType> {
    // Develop A List Of Drug Objects
    const userCytokines = selected.filter(v => v.type === NodeType.CYTOKINE);
    const linkedCytokines = selected.filter(v => v.type === NodeType.KINASE).reduce<Array<Node>>((p, c) => {
      const cytoStrings = this._graph.predecessors(c.id) || [];
      return p.concat(cytoStrings.map(v => new Node(v)))
    }, []);
    const uniqueCytokines = Array.from(new Set(linkedCytokines.concat(userCytokines).map(v => v.name)));
    const drugCytokineScore = uniqueCytokines.reduce<Array<{ drug: string, score: number, cytokine: string }>>((p, c) => {
      return p.concat(this.getDrugs(c).map(w => ({ drug: w.drug, score: w.score, cytokine: c })))
    }, [])

    const excludedCytokines = this._cytokine.filter(x => !uniqueCytokines.includes(x))
    const excludedDrugCytokineScore = excludedCytokines.reduce<Array<{ drug: string, score: number, cytokine: string }>>((p, c) => {
      return p.concat(this.getDrugs(c).map(w => ({ drug: w.drug, score: w.score, cytokine: c })))
    }, [])

    const byDrugs = _.groupBy(drugCytokineScore, 'drug')
    const drugInfo = Object.keys(byDrugs).map((key: string) => {
      const drug = byDrugs[key];
      const numCytokines = drug.length;
      const scores = drug.map(v => v.score);
      return {
        drug: key,
        percent: numCytokines / uniqueCytokines.length,
        numCytokines,
        cytokines: drug.map(v => ({ cytokine: v.cytokine, score: v.score })),
        mean: mean(scores),
        median: median(scores),
        kiss: (linkedCytokines.length > 0 ) ? 'N/A' : this.calculateKISS(key, drugCytokineScore, excludedDrugCytokineScore),
        fdaApproved: this._fdaApproved[this._drugs.indexOf(key)]
      }
    });
    return drugInfo;
  }

  public filterForKinases(selected: Array<Node>): any {
    // Develop A List Of Kinases
    if (selected.length === 0) return {}
    const selectedNames = selected.map(v => v.name)

    // convert to 1s and 0s
    let matrix: Array<Array<number>> = this._kinaseMatrix.map(v => v.map(w => w > 0 ? 1 : 0));

    // count the number of 1s for each kinase row for entries where the cytokine column is in selected - last column of matrix
    // remove any kinase rows where the count is 0
    let kinaseFilteredMatrix = matrix.filter((v: Array<number>) => {
        let inds = selectedNames.map(x => this._kinaseCytokineNames.indexOf(x));
        let filteredV = inds.map(x => v[x]);
        let sum = filteredV.reduce((a: number, b: number) => a + b, 0);
        return sum > 0;
    });
    // cut the kinase names for the rows you just cut
    let newKinaseNames = this._kinase.filter((w: string, i: number) => {
        let v = matrix[i]
        let inds = selectedNames.map(x => this._kinaseCytokineNames.indexOf(x));
        let filteredV = inds.map(x => v[x]);
        let sum = filteredV.reduce((a: number, b: number) => a + b, 0);
        return sum > 0;
    });

    // sum the kinase rows that have value for each cytokine column - last row of matrix
    // remove any cytokine columns that have sum = 0
    let transposedMatrix = kinaseFilteredMatrix[0].map((_, colIndex) => kinaseFilteredMatrix.map(row => row[colIndex])); // transpose
    let cytoFilteredMatrix = transposedMatrix.filter((v: Array<number>) => {
        let sum = v.reduce((a: number, b: number) => a + b, 0);
        return sum > 0;
    });
    // cut the cytokine names for the columns you just cut
    let newCytoNames = this._kinaseCytokineNames.filter((w: string, i: number) => {
        let v = transposedMatrix[i]
        let sum = v.reduce((a: number, b: number) => a + b, 0);
        return sum > 0;
    });
    let newFilteredMatrix = cytoFilteredMatrix[0].map((_, colIndex) => cytoFilteredMatrix.map(row => row[colIndex])); // re-transpose

    // count the entries for each kinase for all cytokines
    const addedEffectTotalCyto = newFilteredMatrix.map(v => {
        let sum = v.reduce((a: number, b: number) => a + b, 0);
        v.push(sum) // add sum to end of array
        return v
      });

    // count the entries for each kinase for the selected cytokines
    const addedEffectCytoOfInterest = addedEffectTotalCyto.map(v => {
        let inds = selectedNames.map(x => newCytoNames.indexOf(x));
        let filteredV = inds.map(x => v[x]);
        let sum = filteredV.reduce((a: number, b: number) => a + b, 0);
        v.push(sum) // add sum to end of array
        return v
      });

    // split the above into rows - one per cytokine
    const kinaseInfo = newCytoNames.map((cytokine: string) => {
      const row = this.getKinases(cytokine, newCytoNames, newKinaseNames, addedEffectCytoOfInterest)
      const kinases = row.map((v: any)=> ({[v.kinase]: v.row}))
      const allKinases = kinases.reduce(((r: any, c: any) => Object.assign(r, c)), {})

      return {
      cytokine,
      ...allKinases,
    }
  });

  const len = addedEffectCytoOfInterest[0].length
  kinaseInfo.push({
    cytokine: "Effect Cytokines of Interest",
    ...newKinaseNames.map((kinase, i) => ({ [kinase]: addedEffectCytoOfInterest[i][len-1]})).reduce(((r, c) => Object.assign(r, c)), {})
  })
  kinaseInfo.push({
      cytokine: "Effect Total Cytokines",
      ...newKinaseNames.map((kinase, i) => ({ [kinase]: addedEffectCytoOfInterest[i][len-2]})).reduce(((r, c) => Object.assign(r, c)), {})
  })

  return kinaseInfo;
}
}